import React, { useEffect, useRef, useState } from "react";
import MaterialIcon from "../BuilderComponents/MaterialIcon";
import { getToolTipAttributes } from "../../utils/tooltip";

const parseIntValue = (value) => parseInt(value, 10) || 0;

const ElementPositionControls = ({ dimensions: propDimensions, onChange, type, gridSize = 10 }) => {
  const [dimensions, setDimensions] = useState({
    top: parseIntValue(propDimensions.top),
    left: parseIntValue(propDimensions.left),
    width: parseIntValue(propDimensions.width),
    height: parseIntValue(propDimensions.height)
  });

  const [editField, setEditField] = useState(null);

  useEffect(() => {
    setDimensions({
      top: parseIntValue(propDimensions.top),
      left: parseIntValue(propDimensions.left),
      width: parseIntValue(propDimensions.width),
      height: parseIntValue(propDimensions.height)
    });
  }, [propDimensions]);

  const handleChange = (key, newVal) => {
    const updatedVal = parseIntValue(newVal);
    const updated = { ...dimensions, [key]: updatedVal };

    setDimensions(updated);

    const filtered = Object.fromEntries(editableFields.map((field) => [field, `${updated[field]}px`]));
    onChange(filtered);
  };

  const handleBlur = () => {
    setEditField(null);
  };

  const handleSnapToGrid = () => {
    const snapped = { ...dimensions };
    editableFields.forEach((field) => {
      snapped[field] = Math.round(snapped[field] / gridSize) * gridSize;
    });

    setDimensions(snapped);

    const filtered = Object.fromEntries(editableFields.map((field) => [field, `${snapped[field]}px`]));
    onChange(filtered);
  };

  const editableFields =
    type === "section" ? [] : type === "column" ? ["left", "width"] : ["top", "left", "width", "height"];

  return (
    <div>
      {editableFields.length > 0 && (
        <div>
          <MaterialIcon
            onClick={handleSnapToGrid}
            name={"view_module"}
            className={"cursor_pointer d_inline"}
            {...getToolTipAttributes("Snap To Grid")}
          />
        </div>
      )}
      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        {["top", "left", "width", "height"].map((key) => (
          <PreviewBox
            key={key}
            label={key[0].toUpperCase() + key.slice(1)}
            value={dimensions[key]}
            isEditing={editField === key}
            editable={editableFields.includes(key)}
            onEnterEditMode={() => editableFields.includes(key) && setEditField(key)}
            onChange={(val) => handleChange(key, val)}
            onBlur={handleBlur}
          />
        ))}
      </div>
    </div>
  );
};

const PreviewBox = ({ label, value, isEditing, editable, onEnterEditMode, onChange, onBlur }) => {
  const inputRef = useRef();
  const [localValue, setLocalValue] = useState(String(value));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isEditing) {
      setLocalValue(String(value));
      inputRef.current?.focus();
    }
  }, [isEditing, value]);

  const validate = (val) => {
    const num = parseFloat(val);
    if (val === "" || isNaN(num)) {
      setError("Must be a number");
      return false;
    }
    setError(null);
    return true;
  };

  const handleInputChange = (val) => {
    // Allow only digits, one dot, and optional leading minus
    const cleaned = val.replace(/[^0-9.-]/g, "");
    const dotCount = (cleaned.match(/\./g) || []).length;
    const minusCount = (cleaned.match(/-/g) || []).length;

    if (dotCount > 1 || minusCount > 1 || (minusCount === 1 && cleaned[0] !== "-")) {
      return; // Ignore this input
    }

    setLocalValue(cleaned);
    if (validate(cleaned)) {
      onChange(parseFloat(cleaned));
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      inputRef.current.blur();
    }
  };

  const handleInputBlur = () => {
    onBlur(); // already validated during input
  };

  const boxStyle = {
    width: 80,
    height: 58,
    borderColor: error ? "#e53935" : "#ccc",
    boxShadow: isEditing ? `0 0 0 2px ${error ? "#e5393511" : "#007bff33"}` : "none"
  };

  const labelStyle = {
    color: error ? "#e53935" : "#666"
  };

  const contentWrapper = {
    height: 20
  };

  const sharedTextStyle = {
    fontSize: 13,
    fontWeight: 600,
    lineHeight: "1.3",
    color: "#222",
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    padding: 0,
    margin: 0,
    border: "none",
    outline: "none",
    background: "transparent",
    display: "flex",
    alignItems: "center"
  };

  const errorStyle = {
    position: "absolute",
    bottom: -14,
    left: 0,
    fontSize: 10,
    color: "#e53935",
    whiteSpace: "nowrap"
  };

  return (
    <div
      style={{
        ...boxStyle,
        cursor: editable ? "pointer" : "not-allowed",
        opacity: editable ? 1 : 0.5,
        backgroundColor: editable ? "#fff" : "#f5f5f5"
      }}
      className={"p_2 border border_radius_medium position_relative dis" + (editable ? " bg_light" : " bg_gray")}
      onClick={editable ? onEnterEditMode : undefined}
    >
      <span style={labelStyle} className={"font_size_xsmall"}>
        {label}
      </span>
      <div style={contentWrapper} className={"position_relative"}>
        <div
          style={{
            ...sharedTextStyle,
            opacity: isEditing ? 0 : 1,
            pointerEvents: isEditing ? "none" : "auto"
          }}
        >
          {value}
        </div>
        <input
          ref={inputRef}
          type="text"
          value={localValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleInputBlur}
          style={{
            ...sharedTextStyle,
            opacity: isEditing ? 1 : 0,
            pointerEvents: isEditing ? "auto" : "none",
            color: error ? "#e53935" : "#222"
          }}
        />
      </div>
      {error && <div style={errorStyle}>{error}</div>}
    </div>
  );
};

export default ElementPositionControls;
